import {
  Fragment,
  ReactNode,
  useState,
} from 'react';
import { When } from 'react-if';
import { useLocalStorage } from 'usehooks-ts';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  ContactPersonSourceType,
  displayPhoneNumber,
  IContactPerson,
  accountingSystemProvider,
} from 'shared';

import { Modal } from '../../../../../common/Atoms/Modal';
import { classNames } from '../../../../../common/lib/classNames';
import { useContact } from '../../../../hooks/useContact';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useUpdateContactPersonTagsMutation } from '../../../../services/api/contactsApi/contact';
import { MobileNumberFixModalContent } from '../../../Modals/MobileNumberFixModal';
import { InvalidMobileFixButton } from '../../CheckMobileNumbersButton';
import { ContactPersons, SourceBadge } from '../ContactPersonsSection/ContactPersons';

import { cardClass } from './ContactSummaryCards';
import {
  CallIconButton,
  callTargetFor,
  ICallTarget,
  useClickToCall,
} from './ClickToCall';
import { TagPills } from './TagPills';

function personName(person: IContactPerson) {
  return `${person.firstName || ``} ${person.lastName || ``}`.trim() || `No name`;
}

function PersonTags({ person }: { person: IContactPerson }) {
  const { data: contact } = useContact();
  const [updateTags, { isLoading }] = useUpdateContactPersonTagsMutation();

  if (!person.id) return null;

  return (
    <span className={ `mt-0.5 flex flex-wrap items-center gap-1` }>
      <TagPills
        small
        tags={ person.personTags || [] }
        onChange={ tags => updateTags({ organisationId: contact.organisationId, contactId: contact.id, personId: person.id, tags }) }
        disabled={ isLoading }
      />
    </span>
  );
}

function IncludedInEmails({ included }: { included: boolean }) {
  return (
    <span className={ classNames(`flex items-center gap-1.5 text-xs`, included ? `text-green-600` : `text-gray-400`) }>
      <span className={ classNames(`h-1.5 w-1.5 rounded-full`, included ? `bg-green-600` : `bg-gray-400`) } />
      { included ? `Included in emails` : `Not included in emails` }
    </span>
  );
}

export function ContactPeopleCard() {
  const { data: contact } = useContact();
  const organisation = useGetSelectedOrganisation();
  const [expanded, setExpanded] = useLocalStorage(`contactPeopleExpanded`, false);
  const [editOpen, setEditOpen] = useState(false);
  const [fixMobileOpen, setFixMobileOpen] = useState(false);
  const { enabled: canCall, browserEnabled, call, busy: callBusy } = useClickToCall();

  if (!contact?.primaryContactPerson) return null;

  const primary = contact.primaryContactPerson;
  const others = contact.contactPersons || [];
  const provider = accountingSystemProvider(organisation);

  function callButton(target: ICallTarget | null) {
    if (!canCall || !target) return null;

    return (
      <CallIconButton
        target={ target }
        browserEnabled={ browserEnabled }
        onCall={ call }
        disabled={ callBusy }
        className={ `ml-0.5` }
      />
    );
  }

  /** Email, phone and (unless it has its own line) mobile, dot separated, each number with a call button */
  function contactLine(person: IContactPerson, mobileOnOwnLine: boolean) {
    const phone = displayPhoneNumber(person.defaultCountryCode, person.defaultNumber);
    const mobile = displayPhoneNumber(person.mobileCountryCode, person.mobileNumber);
    const parts: ReactNode[] = [
      person.email ? <span key={ `email` }>{ person.email }</span> : null,
      phone ? (
        <span
          key={ `ph` }
          className={ `inline-flex items-center` }
        >
          { `Ph ${phone}` }
          { callButton(callTargetFor(person, `default`)) }
        </span>
      ) : null,
      !mobileOnOwnLine && mobile ? (
        <span
          key={ `mob` }
          className={ `inline-flex items-center` }
        >
          { `Mob ${mobile}` }
          { callButton(callTargetFor(person, `mobile`)) }
        </span>
      ) : null,
    ].filter(Boolean);

    if (!parts.length) return null;

    return parts.map((part, i) => (
      <Fragment key={ i }>
        { i > 0 ? <span className={ `mx-1 text-gray-400` }>{ `·` }</span> : null }
        { part }
      </Fragment>
    ));
  }

  const primaryMobile = displayPhoneNumber(primary.mobileCountryCode, primary.mobileNumber);
  const primaryLine = contactLine(primary, true);

  return (
    <div className={ classNames(cardClass, `flex flex-col text-[0.8125rem]`) }>
      <div className={ `flex items-center justify-between px-4 pt-3` }>
        <span className={ `text-sm font-semibold text-gray-900` }>
          { `Contact people ` }
          <span className={ `font-normal text-gray-400` }>{ others.length + 1 }</span>
        </span>
        <button
          type={ `button` }
          onClick={ () => setEditOpen(true) }
          className={ `cursor-pointer text-xs text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
        >
          { `Edit` }
        </button>
      </div>

      <div className={ `flex flex-col gap-1 px-4 pb-3 pt-2` }>
        <span className={ `flex flex-wrap items-center gap-2` }>
          <span className={ `font-semibold text-gray-900` }>{ personName(primary) }</span>
          <SourceBadge
            sourceType={ ContactPersonSourceType.IMPORTED }
            importedProviderLabel={ provider?.displayName ?? `` }
            importedBadgeColor={ provider?.brandColor ?? `` }
          />
          <span className={ `text-xs text-gray-500` }>
            { primary.includeInNotifications ? `Primary · Included in emails` : `Primary · Not included in emails` }
          </span>
        </span>
        <When condition={ !!primaryLine }>
          <span className={ `flex flex-wrap items-center break-words text-gray-700` }>{ primaryLine }</span>
        </When>
        <When condition={ !!primaryMobile }>
          <span className={ `flex items-center text-gray-700` }>
            { `Mob ${primaryMobile}` }
            { callButton(callTargetFor(primary, `mobile`)) }
            <When condition={ !!primary.mobileNumberInvalid }>
              <InvalidMobileFixButton onClick={ () => setFixMobileOpen(true) } />
            </When>
          </span>
        </When>
        <When condition={ !!primary.emailError }>
          <span className={ `text-xs text-orange-700` }>{ primary.emailError }</span>
        </When>
        <PersonTags person={ primary } />
      </div>

      <When condition={ expanded }>
        { others.map((person, i) => {
          const line = contactLine(person, false);

          return (
            <div
              key={ person.id || i }
              className={ `flex flex-col gap-0.5 border-t border-gray-100 px-4 py-2.5` }
            >
              <span className={ `flex flex-wrap items-center gap-2` }>
                <span className={ `font-semibold text-gray-900` }>{ personName(person) }</span>
                <span className={ `text-xs text-gray-500` }>{ `Additional contact` }</span>
              </span>
              <When condition={ !!line }>
                <span className={ `flex flex-wrap items-center break-words text-gray-700` }>{ line }</span>
              </When>
              <IncludedInEmails included={ person.includeInNotifications } />
              <When condition={ !!person.emailError }>
                <span className={ `text-xs text-orange-700` }>{ person.emailError }</span>
              </When>
              <PersonTags person={ person } />
            </div>
          );
        }) }
      </When>

      <When condition={ others.length > 0 }>
        <button
          type={ `button` }
          onClick={ () => setExpanded(!expanded) }
          aria-expanded={ expanded }
          className={ `cursor-pointer flex items-center justify-between rounded-b-xl border-t border-gray-100 px-4 py-2 text-[0.8125rem] text-paidniceGreen-600 hover:bg-gray-50` }
        >
          { expanded ? `Show primary only` : `Show ${others.length} more` }
          <ChevronDownIcon className={ classNames(`h-4 w-4 transition-transform`, expanded && `rotate-180`) } />
        </button>
      </When>

      <Modal
        title={ `Contact people` }
        open={ editOpen }
        setOpen={ setEditOpen }
        width={ `xl` }
      >
        <ContactPersons
          embedded
          editOnly
          onDone={ () => setEditOpen(false) }
        />
      </Modal>

      <MobileNumberFixModalContent
        open={ fixMobileOpen }
        hide={ () => setFixMobileOpen(false) }
        contactId={ contact.id }
      />
    </div>
  );
}
